/**
 * AN ERROR IS NOT AN EMPTY STATE, AND A REFUSAL IS NOT "NONE".
 *
 * The bug class this exists to end has surfaced at least five times and on
 * nine screens. It has one symptom and it is never a crash: a list call fails
 * or is refused, the screen has two branches instead of three, and the user is
 * shown the reassuring one.
 *
 *   /finance/invoices   a failed list read "No invoices in this tab"    (fe#704)
 *   AlertsScreen        a failed load read "All Clear ... up to date"
 *   /time-off           a logistics user's refused list read "No pending
 *                       requests"                                       (fe#697)
 *   QuotesScreen        a dead API read "No quotes found. Create your first
 *                       quote to get started" -- offering a WRITE as the
 *                       remedy for a failed READ                        (fe#573)
 *
 * AlertsScreen is the one worth remembering: it is the screen whose entire job
 * is to tell you something is wrong, and when it could not do that job it said
 * "All Clear". A screen that cannot reach the server knows nothing about
 * certifications, and "up to date" is a claim it has no basis for.
 *
 * THREE STATES, NEVER COLLAPSED INTO ONE:
 *
 *   loaded, genuinely empty  -> "none", worded as a fact about the data
 *   loaded, has results      -> the results
 *   failed or refused        -> what happened, and whether it was permission
 *
 * An empty array IS "none" and should say so plainly. A 403 is not "none". A
 * dropped connection is not "none".
 *
 * WHY THE REFUSAL/FAILURE SPLIT IS THE WHOLE POINT OF THIS MODULE rather than
 * a detail of the wording. The two have different remedies and the reader is
 * the one who has to pick. "Ask an admin for access" is useless advice to
 * someone whose train went into a tunnel, and "try again in a moment" is
 * useless advice to a dispatcher the API will refuse every time for the next
 * year. Collapsing them into "Something went wrong" sends half the readers
 * down the wrong path, which is most of the value of having said anything.
 *
 * This is the em-dash rule's sibling, one layer up. That rule governs a FIELD
 * whose value did not arrive; this governs a LIST that did not arrive. Both
 * refuse to let an absence render as a reassuring number.
 *
 * Related and deliberately kept distinct -- fe#702: a team member with no
 * certifications reads "None on file", NOT "All current". An empty set is a
 * fact about the person. It is not the same as not knowing.
 */

/**
 * What kind of not-arriving this was.
 *
 * Only `refused` requires the server to have said so, for the same reason
 * `classifyAuthFailure` makes that asymmetry: telling someone they lack
 * permission when the real problem is their signal sends them to bother an
 * admin about a working account.
 */
export type LoadFailureKind =
  /** 401/403. The server read the request and declined it. Retrying changes nothing. */
  | 'refused'
  /** Anything else: a timeout, a dropped connection, a 500, a 502 from the edge. */
  | 'unreachable';

/** The status a failed request came back with, if it came back at all. */
function statusOf(error: unknown): number | null {
  const e = error as { response?: { status?: unknown }; status?: unknown } | null | undefined;
  const status = e?.response?.status ?? e?.status;
  return typeof status === 'number' ? status : null;
}

/**
 * 401 as well as 403, and that is not an oversight about session expiry.
 *
 * A 401 reaching a list query means the token was rejected for THIS call. The
 * interceptor's job is to get the reader to /login, but until it does the
 * screen is still mounted and still has to render something, and "no requests
 * found" is the wrong something. Both statuses mean the server answered and
 * said no.
 */
export function isRefusal(error: unknown): boolean {
  const status = statusOf(error);
  return status === 401 || status === 403;
}

/**
 * A 404 on a read of ONE record: the server answered, and the record is not
 * there (wrong id, deleted, another tenant's). A record page says "not found"
 * for it rather than "couldn't reach the server", which would send the reader
 * to retry a link that will never resolve. Lists never see this; a list with
 * nothing in it is a 200 with an empty array.
 */
export function isNotFound(error: unknown): boolean {
  return statusOf(error) === 404;
}

/** Null when nothing failed -- so a call site can pass `error` straight through. */
export function classifyLoadFailure(error: unknown): LoadFailureKind | null {
  if (!error) return null;
  return isRefusal(error) ? 'refused' : 'unreachable';
}

/**
 * Should React Query retry this?
 *
 * A refusal is an answer, not a flake. Retrying one delays the notice by
 * however long the backoff takes and tells the reader nothing new. Matches
 * the policy PayrollSummaryScreen's shop query settled on.
 */
export function retryUnlessRefused(maxAttempts = 2) {
  return (failureCount: number, error: unknown) =>
    !isRefusal(error) && failureCount < maxAttempts;
}

/**
 * THE OPTIONS A THREE-STATE SCREEN NEEDS, AND `networkMode` IS NOT OPTIONAL.
 *
 * MEASURED IN A REAL BROWSER, and it is the reason this constant exists
 * rather than each screen passing `retry` alone. With React Query's default
 * `networkMode: 'online'`, a query that fails and WANTS TO RETRY does not go
 * to the error state — it goes to **paused**:
 *
 *     status: 'pending'   fetchStatus: 'paused'
 *     error: null         data: undefined       isLoading: false
 *
 * Every one of those is the wrong answer for a screen branching on `error`.
 * `error` is null so the failure branch never renders; `data` is undefined so
 * `?? []` produces an empty list; and `isLoading` is FALSE, so the spinner
 * does not hold the screen either. The result is the original bug, exactly:
 * AlertsScreen rendered "All Clear" over three 500s, with no error anywhere
 * in the component's state to notice.
 *
 * It bites precisely the failures that get retried — the 5xx and dropped
 * connections — and NOT a 401/403, which `retryUnlessRefused` declines to
 * retry, so the refusal lands in `error` immediately and looks fine. A screen
 * verified only against a refusal therefore looks completely correct while
 * being unfixed for every other failure. That is how this was nearly shipped:
 * the refusal case rendered perfectly in the browser and the 5xx case still
 * said "All Clear".
 *
 * `'always'` means the query runs and its failure lands in `error`, where the
 * screen can say so. This is the same argument `use-offline-query.ts` makes
 * for mutations — pausing hides the truth and the app already has its own
 * connectivity detector — applied to reads: one connectivity opinion, not
 * two. A genuine dead zone now renders "Couldn't load X. This is a problem
 * reaching the server", which is what a reader in a dead zone should see.
 *
 * Spread it into any query whose screen has a failure branch:
 *
 *     useQuery({ queryKey, queryFn, ...LOAD_FAILURE_QUERY_OPTIONS })
 */
export const LOAD_FAILURE_QUERY_OPTIONS = {
  retry: retryUnlessRefused(),
  networkMode: 'always',
} as const;

/**
 * READ THE FAILURE OFF THE QUERY, NOT OFF `error` ALONE.
 *
 * MEASURED IN A REAL BROWSER against three forced 500s, and it is the reason
 * this function exists at all. A React Query query that fails and is waiting
 * to retry does not sit in the error state — it sits in **paused**:
 *
 *     status: 'pending'      fetchStatus: 'paused'
 *     error: null            failureReason: AxiosError 500
 *     data: undefined        isLoading: false
 *
 * Every field a screen might branch on gives the wrong answer. `error` is
 * null, so a failure branch keyed on it never renders. `data` is undefined,
 * so `?? []` makes an empty list. And `isLoading` is FALSE, so the spinner
 * does not hold the screen either. AlertsScreen rendered "All Clear" over
 * three 500s with nothing in the component's state to notice — the original
 * bug, surviving the first version of its own fix.
 *
 * WHAT MAKES IT DANGEROUS IS WHICH FAILURES IT HIDES. `retryUnlessRefused`
 * declines to retry a 401/403, so a refusal never pauses and lands in `error`
 * immediately. A screen verified only against a refusal therefore looks
 * completely correct while being unfixed for every 5xx and every dropped
 * connection. That is exactly how far this got: the refusal case rendered
 * perfectly in the browser and the 5xx case still said "All Clear".
 *
 * `networkMode: 'always'` in the options above did NOT prevent the pause when
 * measured, so this does not rely on it. `failureReason` carries the error
 * whether the query has settled or is between attempts, and a paused query
 * with a failure behind it is, to the reader, a load that has not arrived.
 */
export function queryLoadFailure(query: {
  error?: unknown;
  fetchStatus?: string;
  failureReason?: unknown;
}): LoadFailureKind | null {
  if (query.error) return classifyLoadFailure(query.error);
  if (query.fetchStatus === 'paused' && query.failureReason) {
    return classifyLoadFailure(query.failureReason);
  }
  return null;
}

/** The error object behind a failure, wherever the query is keeping it. */
export function queryFailureError(query: { error?: unknown; failureReason?: unknown }): unknown {
  return query.error ?? query.failureReason ?? null;
}

/**
 * The sentence to show, built from what the reader was trying to see.
 *
 * `subject` is a lowercase plural noun phrase naming the data, as it would
 * appear mid-sentence: 'invoices', 'time-off requests', 'equipment alerts'.
 * The call site supplies it because only the call site knows what the query
 * was for, and a generic "data" helps nobody.
 *
 * Two lines, always, and the second one carries the load. The heading says
 * what happened; the detail says what it is NOT -- because the failure this
 * whole module exists to fix is a reader inferring a fact about their business
 * from a fact about the network. Saying "this is not a statement about your
 * invoices" out loud is the correction.
 */
export function loadFailureMessage(
  kind: LoadFailureKind,
  subject: string,
  options?: {
    /** The reader's role, rendered as "yours is Dispatcher". Omitted when unknown. */
    roleLabel?: string;
    /** Who is allowed, e.g. 'admins and office staff'. Omitted when we do not know. */
    allowed?: string;
  }
): { heading: string; detail: string } {
  if (kind === 'refused') {
    const who = options?.allowed ? `${options.allowed} can see ${subject}` : `You do not have access to ${subject}`;
    const yours = options?.roleLabel ? ` — yours is ${options.roleLabel}` : '';
    return {
      heading: `You don’t have permission to view ${subject}`,
      detail: `${who}${yours}. This is a statement about what you can see, not about whether there are any.`,
    };
  }
  return {
    heading: `Couldn’t load ${subject}`,
    detail: `This is a problem reaching the server, not a statement about your ${possessive(subject)}. Try again in a moment.`,
  };
}

/**
 * THE SUBJECT AFTER "your", WITH ANY LEADING ARTICLE DROPPED.
 *
 * `subject` is documented as a noun phrase "as it would appear mid-sentence",
 * and both refusal sentences use it that way — "admins can see the payroll
 * summary", "You do not have access to the crew roster" — so ten call sites
 * quite reasonably pass one with a leading article. The unreachable sentence is
 * the only one that puts a POSSESSIVE in front of it, and there the same value
 * reads "not a statement about **your the payroll summary**".
 *
 * Found 2026-09-23 by forcing a 500 through the dev proxy while proving
 * fe#760's two report screens. It had shipped on every screen that passes an
 * article — the payroll summary, the crew roster, the torque sheet queue, the
 * fleet's due status, the time-off calendar, the intake queue, the asset list,
 * the financial summary, the per-unit maintenance roll-up, the month's figures
 * — and on none of the others, which is why it survived: a reviewer reading
 * `subject="receivables"` at a call site sees correct copy, and the broken half
 * only appears when a 5xx is forced on a screen whose subject has an article.
 *
 * FIXED HERE RATHER THAN AT TEN CALL SITES, and deliberately not by rewording
 * the ten. The article is CORRECT in the two refusal sentences; stripping it
 * from the call sites to satisfy this one would break those. The grammar of
 * each sentence is this module's business, which is the whole reason the
 * sentences live here instead of on the screens.
 */
function possessive(subject: string): string {
  return subject.replace(/^(the|a|an)\s+/i, '');
}
